import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import customerJourneyService, { 
  CustomerBehaviorAnalysis, 
  CustomerPreference, 
  CustomerFeedback,
  NotificationData 
} from '@/services/customerJourneyService';

export const useCustomerJourney = () => {
  const { user } = useAuth();
  const [behaviorAnalysis, setBehaviorAnalysis] = useState<CustomerBehaviorAnalysis | null>(null);
  const [churnRisk, setChurnRisk] = useState<number>(0);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState(false);

  // Track events
  const trackEvent = useCallback(async (eventType: string, eventData: Record<string, unknown> = {}) => {
    await customerJourneyService.trackEvent(eventType, eventData);
  }, []);

  // Convenience tracking methods
  const trackPageView = useCallback(async (pageName: string, pageData: Record<string, unknown> = {}) => {
    await customerJourneyService.trackPageView(pageName, pageData);
  }, []);

  const trackProductView = useCallback(async (productId: string, productData: Record<string, unknown> = {}) => {
    await customerJourneyService.trackProductView(productId, productData);
  }, []);

  const trackAddToCart = useCallback(async (productId: string, quantity: number, productData: Record<string, unknown> = {}) => {
    await customerJourneyService.trackAddToCart(productId, quantity, productData);
  }, []);

  const trackSearch = useCallback(async (query: string, resultsCount: number, filters: Record<string, unknown> = {}) => {
    await customerJourneyService.trackSearch(query, resultsCount, filters);
  }, []);

  const trackCheckoutStart = useCallback(async (orderData: Record<string, unknown>) => {
    await customerJourneyService.trackCheckoutStart(orderData);
  }, []);

  // Get behavior analysis
  const getBehaviorAnalysis = useCallback(async (userId?: string, lookbackDays: number = 30) => {
    setLoading(true);
    try {
      const analysis = await customerJourneyService.getBehaviorAnalysis(userId, lookbackDays);
      setBehaviorAnalysis(analysis);
      return analysis;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get churn risk
  const getChurnRisk = useCallback(async (userId?: string) => {
    const risk = await customerJourneyService.getChurnRisk(userId);
    setChurnRisk(risk);
    return risk;
  }, []);

  // Preferences management
  const savePreference = useCallback(async (customerId: string, preference: CustomerPreference) => {
    await customerJourneyService.savePreference(customerId, preference);
  }, []);

  const getPreferences = useCallback(async (customerId: string, preferenceType?: string) => {
    return await customerJourneyService.getPreferences(customerId, preferenceType);
  }, []);

  // Feedback management
  const submitFeedback = useCallback(async (customerId: string, feedback: CustomerFeedback) => {
    await customerJourneyService.submitFeedback(customerId, feedback);
  }, []);

  const getFeedback = useCallback(async (customerId: string, limit: number = 10) => {
    return await customerJourneyService.getFeedback(customerId, limit);
  }, []);

  // Notifications management
  const createNotification = useCallback(async (userId: string, notification: NotificationData) => {
    await customerJourneyService.createNotification(userId, notification);
  }, []);

  const getNotifications = useCallback(async (userId?: string, limit: number = 20) => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) return [];

    const userNotifications = await customerJourneyService.getNotifications(targetUserId, limit);
    setNotifications(userNotifications);
    return userNotifications;
  }, [user?.id]);

  const markNotificationAsRead = useCallback(async (notificationId: string) => {
    await customerJourneyService.markNotificationAsRead(notificationId);
    // Refresh notifications after marking as read
    await getNotifications();
  }, [getNotifications]);

  // Get comprehensive user view
  const getUserComprehensiveView = useCallback(async (userId?: string) => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) return null;

    return await customerJourneyService.getUserComprehensiveView(targetUserId);
  }, [user?.id]);

  // Auto-sync anonymous events when user logs in
  useEffect(() => {
    if (user?.id) {
      customerJourneyService.syncAnonymousEvents();
    }
  }, [user?.id]);

  // Auto-load user data when user changes
  useEffect(() => {
    if (user?.id) {
      getBehaviorAnalysis();
      getChurnRisk();
      getNotifications();
    }
  }, [user?.id, getBehaviorAnalysis, getChurnRisk, getNotifications]);

  return {
    // Tracking methods
    trackEvent,
    trackPageView,
    trackProductView,
    trackAddToCart,
    trackSearch,
    trackCheckoutStart,

    // Analytics methods
    getBehaviorAnalysis,
    getChurnRisk,
    getUserComprehensiveView,

    // Preferences methods
    savePreference,
    getPreferences,

    // Feedback methods
    submitFeedback,
    getFeedback,

    // Notifications methods
    createNotification,
    getNotifications,
    markNotificationAsRead,

    // State
    behaviorAnalysis,
    churnRisk,
    notifications,
    loading,

    // Computed values
    unreadNotifications: notifications.filter(n => !n.is_read).length,
    engagementLevel: behaviorAnalysis ? 
      behaviorAnalysis.engagement_score > 80 ? 'high' :
      behaviorAnalysis.engagement_score > 40 ? 'medium' : 'low'
      : 'unknown',
    churnRiskLevel: churnRisk > 0.7 ? 'high' : churnRisk > 0.4 ? 'medium' : 'low'
  };
};

export default useCustomerJourney;